import { FunctionComponent, useState } from 'react'
import { DaycarePredefinedMessage, DaycarePredefinedMessageType } from '@/types/Daycare'
import styles from '../customisation.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { ResetTv } from '@mui/icons-material'
import TextArea from '@/components/_custom/TextArea/textArea'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { useMutation } from '@apollo/client'
import { SET_PREDEFINED_MESSAGE } from '@/modules/API/daycare/daycareHandler'
import { useNotification } from '@/modules/context/NotificationContext'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'

interface PredefinedMessageProps {
    messageType: DaycarePredefinedMessageType
    onClose?: () => void
}

const PredefinedMessage: FunctionComponent<PredefinedMessageProps> = ({
    messageType,
    onClose
}: PredefinedMessageProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [setPredefinedMessage] = useMutation(SET_PREDEFINED_MESSAGE, {
        onCompleted: () => {
            fetchCurrentDaycare()
            showNotificationSuccess(t('predefinedMessageUpdated'))
            onClose?.()
        },
        onError: () => {
            showNotificationError(t('predefinedMessageUpdatedError'))
        }
    })

    function handleSetPredefinedMessage(type: DaycarePredefinedMessageType, message: string) {
        setPredefinedMessage({
            variables: {
                daycareId: daycare.id,
                type: type,
                message: message
            }
        })
    }

    const messageTitle: string = defineMessageTitle(messageType as DaycarePredefinedMessageType, t)

    const daycareMessage: DaycarePredefinedMessage | undefined = daycare.predefinedMessages?.find(
        (predefinedMessage: DaycarePredefinedMessage): DaycarePredefinedMessage | undefined =>
            predefinedMessage.type === (messageType as DaycarePredefinedMessageType) ? predefinedMessage : undefined
    )

    const [message, setMessage] = useState<string>(daycareMessage?.message || '')

    function handleReset(): void {
        setMessage('')
        handleSetPredefinedMessage(messageType, '')
    }

    function handleSetMessage(messageInput: string): void {
        setMessage(messageInput)
        handleSetPredefinedMessage(messageType, messageInput)
    }

    return (
        <div className={styles.PredefinedMessagePlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <p className={styles.Title}>{messageTitle}</p>
                </div>
                <div className={styles.InputContainer}>
                    <TextArea
                        id={messageType}
                        value={message}
                        disabled={!user?.currentDaycareRights.canWriteParamsDaycare}
                        onChange={setMessage}
                        maxCount={200}
                        minRows={2}
                        maxRows={3}
                        className={styles.TextArea}
                    />
                    {user?.currentDaycareRights.canWriteParamsDaycare && (
                        <DialogActions>
                            <ButtonTextIcon
                                type={'custom'}
                                onClick={handleReset}
                                customText={t('reset')}
                                customIcon={<ResetTv />}
                                nowrap
                            />
                            <ButtonTextIcon
                                type={'update'}
                                onClick={() => handleSetMessage(message)}
                                disabled={daycareMessage?.message === message}
                                nowrap
                            />
                        </DialogActions>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PredefinedMessage

function defineMessageTitle(messageType: DaycarePredefinedMessageType, t: (key: string) => string): string {
    switch (messageType) {
        case DaycarePredefinedMessageType.acceptRegularRequest:
            return t('acceptRegularPredefinedMessage')
        case DaycarePredefinedMessageType.rejectRegularRequest:
            return t('rejectRegularPredefinedMessage')
        case DaycarePredefinedMessageType.declareAbsenceRegularSlot:
            return t('declareRegularPredefinedMessage')
        case DaycarePredefinedMessageType.cancelOccasionalSlot:
            return t('unbookReason')
        case DaycarePredefinedMessageType.acceptRequestSlot:
            return t('acceptRequestSlot')
        case DaycarePredefinedMessageType.rejectRequestSlot:
            return t('refuseRequestSlot')
        default:
            return t('generalError')
    }
}
